import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import { updateBowlerAverage } from "../../utility/dataOperations/updateBowlerAverage";
import { useTournamentVerification } from "../../hooks/useTournamentVerification";
import { useAuth } from "../../context/AuthContext";
import {
  DEFAULT_MIN_GAMES_FOR_AVERAGE,
  getTournamentAverageCatalog,
  listCatalogExtractedPeople,
} from "../../utility/averageCatalogs";
import { buildCatalogMatchesByBowler } from "../../utility/applyCatalogToAverages";
import { signupProofSheets } from "../../utility/signupAverageProof";

export const bowlerIsOnAnActiveTeam = (bowlerId, bowlerTeamInfo) =>
  (bowlerTeamInfo?.[bowlerId] || []).length > 0;

export const useBowlerAverages = (tournamentId, { canManage = false } = {}) => {
  const { user } = useAuth();
  // Core data state
  const [averages, setAverages] = useState(null);
  const [bowlers, setBowlers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [editingBowlerId, setEditingBowlerId] = useState(null);
  const [editedAverage, setEditedAverage] = useState("");
  const [editedHasAverage, setEditedHasAverage] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [verifyingBowlerId, setVerifyingBowlerId] = useState(null);
  const [verifyingAll, setVerifyingAll] = useState(false);
  const [localVerificationState, setLocalVerificationState] = useState({});
  const [bowlerTeamInfo, setBowlerTeamInfo] = useState(null);
  const [catalogInfo, setCatalogInfo] = useState(null);
  const [catalogByBowler, setCatalogByBowler] = useState({});
  const [proofsByBowler, setProofsByBowler] = useState({});

  // Verification system
  const {
    verificationStatus,
    loading: verificationLoading,
    error: verificationError,
    canVerifyBowler,
    canUnverifyBowler,
  } = useTournamentVerification(tournamentId);

  // Derived state using useMemo for performance
  const tournamentBowlers = useMemo(() => {
    if (!averages || !bowlers || !bowlerTeamInfo) return null;

    // Create bowler map for efficient lookups
    const bowlerMap = bowlers.reduce((acc, bowler) => {
      acc[bowler.id] = bowler.name;
      return acc;
    }, {});

    // Process averages with verification status
    const bowlerData = averages
      .filter((avg) => bowlerIsOnAnActiveTeam(avg.bowlerID, bowlerTeamInfo))
      .map((avg) => {
        const bowlerName = bowlerMap[avg.bowlerID];
        const verificationState = verificationStatus?.averages?.find(
          (vs) => vs.entityId === avg.bowlerID
        );

        // Prioritize local verification state over hook's verification status
        const isVerified =
          localVerificationState[avg.bowlerID] !== undefined
            ? localVerificationState[avg.bowlerID]
            : verificationState?.isVerified || false;

        // Check if this bowler is on any teams with verified scores
        const bowlerTeams = bowlerTeamInfo[avg.bowlerID] || [];
        const hasVerifiedTeamScores = bowlerTeams.some((teamId) => {
          return verificationStatus?.scores?.some(
            (score) => score.entityId === teamId && score.isVerified
          );
        });
        const catalog = catalogByBowler[avg.bowlerID] || null;

        return {
          bowlerId: avg.bowlerID,
          bowlerName,
          average: avg.average,
          hasAverage: avg.hasAverage !== false, // Default to true for backward compatibility
          isVerified,
          hasVerifiedTeamScores,
          catalog,
          signupProofs: signupProofSheets(proofsByBowler[avg.bowlerID]),
          needsAttention:
            !isVerified &&
            catalog &&
          [
            "disagrees",
            "possibleMatch",
            "noQualifyingAverage",
            "usbcConflict",
          ].includes(catalog.kind),
        };
      });

    return { bowlerData };
  }, [
    averages,
    bowlers,
    verificationStatus,
    localVerificationState,
    bowlerTeamInfo,
    catalogByBowler,
    proofsByBowler,
  ]);

  // Clear local verification state when hook's verification status updates
  useEffect(() => {
    if (verificationStatus?.averages) {
      // Clear local state for any bowlers that have been updated by the hook
      setLocalVerificationState((prev) => {
        const updated = { ...prev };
        verificationStatus.averages.forEach((avg) => {
          if (updated[avg.entityId] !== undefined) {
            delete updated[avg.entityId];
          }
        });
        return updated;
      });
    }
  }, [verificationStatus?.averages]);

  // Fetch core data
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        // Get tournament organization
        const { data: tournament, error: tournamentError } = await supabase
          .from("tournaments")
          .select("organizationID")
          .eq("id", tournamentId)
          .single();

        if (tournamentError) throw tournamentError;

        // Fetch bowlers and averages in parallel
        const [bowlersResult, averagesResult, catalogLink] = await Promise.all([
          supabase
            .from("bowlers")
            .select("id, name, usbcID, created_at")
            .eq("organizationID", tournament.organizationID)
            .is("deletedAt", null),
          supabase
            .from("tournamentAverages")
            .select("*")
            .eq("tournamentID", tournamentId),
          getTournamentAverageCatalog(tournamentId).catch(() => null),
        ]);

        if (bowlersResult.error) throw bowlersResult.error;
        if (averagesResult.error) throw averagesResult.error;

        setBowlers(bowlersResult.data);
        setAverages(averagesResult.data);

        if (catalogLink?.catalogID) {
          const people = await listCatalogExtractedPeople(
            catalogLink.catalogID,
            tournament.organizationID
          );
          setCatalogInfo({
            catalogID: catalogLink.catalogID,
            name: catalogLink.catalog?.name || "Average catalog",
            minGamesForAverage:
              catalogLink.minGamesForAverage || DEFAULT_MIN_GAMES_FOR_AVERAGE,
            people,
            organizationID: tournament.organizationID,
          });
        } else {
          setCatalogInfo(null);
          setCatalogByBowler({});
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    if (tournamentId) {
      fetchData();
    }
  }, [tournamentId]);

  // Fetch team information for bowlers. Averages only lists people still on a team.
  useEffect(() => {
    let cancelled = false;

    async function fetchBowlerTeamInfo() {
      if (!tournamentId || bowlers === null) return;

      if (!bowlers.length) {
        if (!cancelled) setBowlerTeamInfo({});
        return;
      }

      try {
        const { data: teams, error: teamsError } = await supabase
          .from("teams")
          .select(
            `
            id,
            squadID,
            isDeleted,
            squads!inner(tournamentID),
            teamassignments!inner(bowlerID)
          `
          )
          .eq("squads.tournamentID", tournamentId)
          .eq("isDeleted", false);

        if (teamsError) throw teamsError;

        const bowlerTeams = {};
        (teams || []).forEach((team) => {
          team.teamassignments.forEach((assignment) => {
            const bowlerId = assignment.bowlerID;
            if (!bowlerTeams[bowlerId]) {
              bowlerTeams[bowlerId] = [];
            }
            bowlerTeams[bowlerId].push(team.id);
          });
        });

        if (!cancelled) setBowlerTeamInfo(bowlerTeams);
      } catch (error) {
        console.error("Error fetching bowler team info:", error);
        if (!cancelled) setBowlerTeamInfo({});
      }
    }

    setBowlerTeamInfo(null);
    fetchBowlerTeamInfo();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, bowlers]);

  useEffect(() => {
    let cancelled = false;

    async function fetchSignupProofs() {
      if (!canManage || !tournamentId) {
        if (!cancelled) setProofsByBowler({});
        return;
      }
      const { data, error: proofError } = await supabase.rpc(
        "get_signup_average_proofs",
        { p_tournament_id: tournamentId }
      );
      if (cancelled) return;
      if (proofError) {
        console.error(proofError);
        setProofsByBowler({});
        return;
      }
      const grouped = {};
      for (const row of data?.proofs || []) {
        if (!row?.bowlerId || !row?.storagePath) continue;
        if (!grouped[row.bowlerId]) grouped[row.bowlerId] = [];
        if (!grouped[row.bowlerId].includes(row.storagePath)) {
          grouped[row.bowlerId].push(row.storagePath);
        }
      }
      setProofsByBowler(grouped);
    }

    fetchSignupProofs();
    return () => {
      cancelled = true;
    };
  }, [canManage, tournamentId]);

  useEffect(() => {
    if (!catalogInfo?.people || !bowlers || !averages || !bowlerTeamInfo) return;

    const roster = bowlers
      .filter(
        (bowler) =>
          bowlerIsOnAnActiveTeam(bowler.id, bowlerTeamInfo) &&
          averages.some((average) => average.bowlerID === bowler.id)
      )
      .map((bowler) => ({
        id: bowler.id,
        name: bowler.name,
        usbcID: bowler.usbcID,
      }));
    const averagesByBowlerId = new Map(
      averages.map((row) => [row.bowlerID, row])
    );
    const preview = {};
    buildCatalogMatchesByBowler({
      tournamentBowlers: roster,
      catalogPeople: catalogInfo.people,
      minGamesForAverage: catalogInfo.minGamesForAverage,
      averagesByBowlerId,
    }).forEach((value, id) => {
      preview[id] = value;
    });
    setCatalogByBowler(preview);
  }, [catalogInfo, bowlers, averages, bowlerTeamInfo]);

  // Event handlers using useCallback for performance
  const handleEditClick = useCallback((bowler) => {
    setEditingBowlerId(bowler.bowlerId);
    // Handle null averages properly
    setEditedAverage(bowler.average ? bowler.average.toString() : "");
    setEditedHasAverage(bowler.hasAverage !== false); // Set hasAverage state from bowler
    setErrorMessage("");
  }, []);

  const handleAverageChange = useCallback((e) => {
    const value = e.target.value;
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 300)) {
      setEditedAverage(value);
      setErrorMessage("");
    } else {
      setErrorMessage("Average must be between 0 and 300");
    }
  }, []);

  const handleSaveClick = useCallback(
    async (bowlerId) => {
      try {
        // Determine the final values based on edited state
        const finalHasAverage = editedHasAverage;
        let finalAverage = null;

        if (finalHasAverage) {
          // If bowler has average, use the edited average value
          finalAverage = editedAverage === "" ? null : parseInt(editedAverage);

          // Validate average if bowler has average
          if (
            finalAverage === null ||
            isNaN(finalAverage) ||
            finalAverage < 0 ||
            finalAverage > 300
          ) {
            setErrorMessage(
              "Average must be between 0 and 300 when bowler has average"
            );
            return;
          }
        }

        const { error } = await updateBowlerAverage(
          bowlerId,
          tournamentId,
          finalAverage,
          finalHasAverage
        );

        if (error) {
          setErrorMessage(error.message);
          return;
        }

        // Update local state
        setAverages((prev) =>
          prev?.map((avg) =>
            avg.bowlerID === bowlerId
              ? { ...avg, average: finalAverage, hasAverage: finalHasAverage }
              : avg
          )
        );

        setEditingBowlerId(null);
        setEditedAverage("");
        setEditedHasAverage(true); // Reset hasAverage state
        setErrorMessage("");
      } catch (error) {
        console.error("Error updating average:", error);
        setErrorMessage("Error saving average. Please try again.");
      }
    },
    [editedAverage, editedHasAverage, tournamentId]
  );

  const handleCancelClick = useCallback(() => {
    setEditingBowlerId(null);
    setEditedAverage("");
    setEditedHasAverage(true); // Reset hasAverage state
    setErrorMessage("");
  }, []);

  const handleVerifyClick = useCallback(
    async (bowlerId, isVerified) => {
      try {
        // Set loading state
        setVerifyingBowlerId(bowlerId);
        setErrorMessage("");

        const newVerifiedStatus = !isVerified;

        const { error } = await supabase.from("verificationStates").upsert(
          {
            tournamentId,
            entityType: "averages",
            entityId: bowlerId,
            isVerified: newVerifiedStatus,
            verifiedAt: newVerifiedStatus ? new Date().toISOString() : null,
          },
          {
            onConflict: "tournamentId,entityType,entityId",
          }
        );

        if (error) {
          console.error("Error updating verification:", error);
          throw error;
        }

        // Store local verification state for immediate UI feedback
        setLocalVerificationState((prev) => ({
          ...prev,
          [bowlerId]: newVerifiedStatus,
        }));
      } catch (error) {
        console.error("Error updating verification status:", error);
        setErrorMessage(
          "Failed to update verification status. Please try again."
        );
      } finally {
        // Clear loading state
        setVerifyingBowlerId(null);
      }
    },
    [tournamentId]
  );

  const handleUseCatalogAverage = useCallback(
    async (bowlerId) => {
      const row = catalogByBowler[bowlerId];
      if (!row || row.average == null) return;
      setVerifyingBowlerId(bowlerId);
      setErrorMessage("");
      try {
        const { error } = await updateBowlerAverage(
          bowlerId,
          tournamentId,
          row.average,
          true
        );
        if (error) throw error;
        setAverages((prev) =>
          prev?.map((avg) =>
            avg.bowlerID === bowlerId
              ? { ...avg, average: row.average, hasAverage: true }
              : avg
          )
        );
      } catch (err) {
        console.error(err);
        setErrorMessage(
          err.message || "Could not use the catalog average."
        );
      } finally {
        setVerifyingBowlerId(null);
      }
    },
    [catalogByBowler, tournamentId]
  );

  const handleReviewCommit = useCallback(
    async (row, values) => {
      const bowlerId = row.bowlerId;
      const average = values.hasAverage === false ? null : values.average;
      const hasAverage = values.hasAverage !== false;
      const { error } = await updateBowlerAverage(
        bowlerId,
        tournamentId,
        average,
        hasAverage
      );
      if (error) throw error;
      const { error: verifyError } = await supabase
        .from("verificationStates")
        .upsert(
          {
            tournamentId,
            entityType: "averages",
            entityId: bowlerId,
            isVerified: true,
            verifiedAt: new Date().toISOString(),
            verifiedBy: user?.id || null,
          },
          { onConflict: "tournamentId,entityType,entityId" }
        );
      if (verifyError) throw verifyError;
      setAverages((prev) =>
        prev?.map((avg) =>
          avg.bowlerID === bowlerId
            ? { ...avg, average, hasAverage }
            : avg
        )
      );
      setLocalVerificationState((prev) => ({ ...prev, [bowlerId]: true }));
    },
    [tournamentId, user?.id]
  );

  const handleVerifyAll = useCallback(async () => {
    if (!tournamentBowlers?.bowlerData?.length) return;

    const unverified = tournamentBowlers.bowlerData.filter(
      (bowler) => !bowler.isVerified
    );
    if (unverified.length === 0) return;

    try {
      setVerifyingAll(true);
      setErrorMessage("");

      const verifiedAt = new Date().toISOString();
      const { error } = await supabase.from("verificationStates").upsert(
        unverified.map((bowler) => ({
          tournamentId,
          entityType: "averages",
          entityId: bowler.bowlerId,
          isVerified: true,
          verifiedAt,
        })),
        {
          onConflict: "tournamentId,entityType,entityId",
        }
      );

      if (error) {
        console.error("Error verifying all averages:", error);
        throw error;
      }

      setLocalVerificationState((prev) => {
        const next = { ...prev };
        unverified.forEach((bowler) => {
          next[bowler.bowlerId] = true;
        });
        return next;
      });
    } catch (error) {
      console.error("Error verifying all averages:", error);
      setErrorMessage("Failed to verify all averages. Please try again.");
    } finally {
      setVerifyingAll(false);
    }
  }, [tournamentBowlers, tournamentId]);

  const handleHasAverageChange = useCallback((hasAverage) => {
    // Only update local state when editing, don't save to database yet
    setEditedHasAverage(hasAverage);

    // If turning off average, clear the average input
    if (!hasAverage) {
      setEditedAverage("");
    }

    // Clear any error messages when toggling
    setErrorMessage("");
  }, []);

  // Helper functions
  const canVerifyBowlerForEdit = useCallback(
    (bowlerId) => {
      if (!tournamentBowlers) return false;

      const bowler = tournamentBowlers.bowlerData.find(
        (b) => b.bowlerId === bowlerId
      );

      return bowler ? !bowler.isVerified : false;
    },
    [tournamentBowlers]
  );

  const canUnverifyBowlerForEdit = useCallback(
    (bowlerId) => {
      if (!tournamentBowlers) return false;

      const bowler = tournamentBowlers.bowlerData.find(
        (b) => b.bowlerId === bowlerId
      );

      // Always allow unverification if bowler is verified
      // Team score verification will only disable the button, not hide it
      return bowler ? bowler.isVerified : false;
    },
    [tournamentBowlers]
  );

  return {
    // State
    loading: loading || verificationLoading || bowlerTeamInfo === null,
    error: error || verificationError,
    tournamentBowlers,

    // UI state
    editingBowlerId,
    editedAverage,
    editedHasAverage,
    errorMessage,
    verifyingBowlerId,
    verifyingAll,
    catalogInfo,

    // Event handlers
    handleEditClick,
    handleAverageChange,
    handleSaveClick,
    handleCancelClick,
    handleVerifyClick,
    handleVerifyAll,
    handleHasAverageChange,
    handleUseCatalogAverage,
    handleReviewCommit,

    // Helper functions
    canVerifyBowlerForEdit,
    canUnverifyBowlerForEdit,
  };
};
